import { bind } from 'bind-event-listener';

import { ACTION, ACTION_SUBJECT_ID } from '@atlaskit/editor-common/analytics';
import type { DispatchAnalyticsEvent } from '@atlaskit/editor-common/analytics';
import {
	Experience,
	EXPERIENCE_ID,
	ExperienceCheckDomMutation,
	ExperienceCheckTimeout,
	getNodeQuery,
	getPopupContainerFromEditorView,
	popupWithNestedElement,
	getSelectionAncestorDOM,
} from '@atlaskit/editor-common/experiences';
import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import { PluginKey } from '@atlaskit/editor-prosemirror/state';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';

import { SYNCED_BLOCK_BUTTON_TEST_ID } from '../types';

import { syncedBlockPluginKey } from './main';

const TIMEOUT_DURATION = 30000;

const pluginKey = new PluginKey('syncedBlockMenuAndToolbarExperience');

const SYNCED_BLOCK_BUTTON_TEST_IDS = Object.values(SYNCED_BLOCK_BUTTON_TEST_ID);

type SyncedBlockButtonId = (typeof SYNCED_BLOCK_BUTTON_TEST_IDS)[number];

const syncedBlockButtonIds = new Set<SyncedBlockButtonId>(SYNCED_BLOCK_BUTTON_TEST_IDS);

type ExperienceOptions = {
	dispatchAnalyticsEvent: DispatchAnalyticsEvent;
	refs: {
		containerElement?: HTMLElement;
		popupsMountPoint?: HTMLElement;
		wrapperElement?: HTMLElement;
	};
};
type EditorViewRef = Record<'current', EditorView | undefined>;

export const getMenuAndToolbarExperiencesPlugin = ({
	refs,
	dispatchAnalyticsEvent,
}: ExperienceOptions): SafePlugin => {
	// Cache the experiment value once at plugin creation time.
	// This fires the exposure event exactly once (correct per Exposure Events 101)
	// and avoids redundant Statsig SDK evaluations in the view() and update() hooks.
	const isPerfExperimentOn = expValEquals('editor_synced_block_perf', 'isEnabled', true);

	let popupsTargetEl: HTMLElement | undefined;
	const editorViewRef: EditorViewRef = { current: undefined };

	const getPopupsTarget = () => {
		if (!popupsTargetEl) {
			popupsTargetEl =
				refs.popupsMountPoint ||
				refs.wrapperElement ||
				getPopupContainerFromEditorView(editorViewRef?.current?.dom);
		}
		return popupsTargetEl;
	};

	const createSourcePrimaryToolbarExperience = new Experience(EXPERIENCE_ID.TOOLBAR_ACTION, {
		action: ACTION.SYNCED_BLOCK_CREATE,
		actionSubjectId: ACTION_SUBJECT_ID.PRIMARY_TOOLBAR,
		dispatchAnalyticsEvent,
		checks: [
			new ExperienceCheckTimeout({ durationMs: TIMEOUT_DURATION }),
			syncedBlockAddedToDomCheck(refs, editorViewRef),
		],
	});

	const createSourceBlockMenuExperience = new Experience(EXPERIENCE_ID.MENU_ACTION, {
		action: ACTION.SYNCED_BLOCK_CREATE,
		actionSubjectId: ACTION_SUBJECT_ID.BLOCK_MENU,
		dispatchAnalyticsEvent,
		checks: [
			new ExperienceCheckTimeout({ durationMs: TIMEOUT_DURATION }),
			syncedBlockAddedToDomCheck(refs, editorViewRef),
		],
	});

	const createSourceQuickInsertMenuExperience = new Experience(EXPERIENCE_ID.MENU_ACTION, {
		action: ACTION.SYNCED_BLOCK_CREATE,
		actionSubjectId: ACTION_SUBJECT_ID.QUICK_INSERT,
		dispatchAnalyticsEvent,
		checks: [
			new ExperienceCheckTimeout({ durationMs: TIMEOUT_DURATION }),
			syncedBlockAddedToDomCheck(refs, editorViewRef),
		],
	});

	const deleteReferenceSyncedBlockExperience = new Experience(EXPERIENCE_ID.TOOLBAR_ACTION, {
		action: ACTION.REFERENCE_SYNCED_BLOCK_DELETE,
		actionSubjectId: ACTION_SUBJECT_ID.SYNCED_BLOCK_TOOLBAR,
		dispatchAnalyticsEvent,
		checks: [
			new ExperienceCheckTimeout({ durationMs: TIMEOUT_DURATION }),
			referenceSyncBlockRemovedFromDomCheck(refs, editorViewRef),
		],
	});

	const unsyncReferenceSyncedBlockExperience = new Experience(EXPERIENCE_ID.TOOLBAR_ACTION, {
		action: ACTION.REFERENCE_SYNCED_BLOCK_UNSYNC,
		actionSubjectId: ACTION_SUBJECT_ID.SYNCED_BLOCK_TOOLBAR,
		dispatchAnalyticsEvent,
		checks: [
			new ExperienceCheckTimeout({ durationMs: TIMEOUT_DURATION }),
			referenceSyncBlockRemovedFromDomCheck(refs, editorViewRef),
		],
	});

	const unsyncSourceSyncedBlockExperience = new Experience(EXPERIENCE_ID.TOOLBAR_ACTION, {
		action: ACTION.SYNCED_BLOCK_UNSYNC,
		actionSubjectId: ACTION_SUBJECT_ID.SYNCED_BLOCK_TOOLBAR,
		dispatchAnalyticsEvent,
		checks: [
			new ExperienceCheckTimeout({ durationMs: TIMEOUT_DURATION }),
			syncBlockDeleteConfirmationModalAddedCheck(),
		],
	});

	const deleteSourceSyncedBlockExperience = new Experience(EXPERIENCE_ID.TOOLBAR_ACTION, {
		action: ACTION.SYNCED_BLOCK_DELETE,
		actionSubjectId: ACTION_SUBJECT_ID.SYNCED_BLOCK_TOOLBAR,
		dispatchAnalyticsEvent,
		checks: [
			new ExperienceCheckTimeout({ durationMs: TIMEOUT_DURATION }),
			syncBlockDeleteConfirmationModalAddedCheck(),
		],
	});

	const syncedLocationsExperience = new Experience(EXPERIENCE_ID.TOOLBAR_ACTION, {
		action: ACTION.SYNCED_BLOCK_VIEW_SYNCED_LOCATIONS,
		actionSubjectId: ACTION_SUBJECT_ID.SYNCED_BLOCK_TOOLBAR,
		dispatchAnalyticsEvent,
		checks: [
			new ExperienceCheckTimeout({ durationMs: TIMEOUT_DURATION }),
			syncedLocationsDropdownOpenedCheck(),
		],
	});

	const bindListeners = () => {
		const unbindClickListener = bind(document, {
			type: 'click',
			listener: (event: MouseEvent) => {
				const target = event.target as Element | null;
				if (!target) {
					return;
				}

				const button = target.closest('button[data-testid]');
				if (!button || !(button instanceof HTMLButtonElement)) {
					return;
				}

				const testId = button.dataset.testid;
				if (!isSyncedBlockButtonId(testId)) {
					return;
				}

				if (button.disabled) {
					return;
				}

				handleButtonClick({
					testId,
					button,
					createSourcePrimaryToolbarExperience,
					createSourceBlockMenuExperience,
					createSourceQuickInsertMenuExperience,
					deleteReferenceSyncedBlockExperience,
					unsyncReferenceSyncedBlockExperience,
					unsyncSourceSyncedBlockExperience,
					deleteSourceSyncedBlockExperience,
					syncedLocationsExperience,
				});
			},
			options: { capture: true },
		});

		const unbindKeydownListener = bind(document, {
			type: 'keydown',
			listener: (event: KeyboardEvent) => {
				if (isEnterKey(event.key)) {
					const typeaheadPopup = popupWithNestedElement(
						getPopupsTarget(),
						'.fabric-editor-typeahead',
					);
					if (!typeaheadPopup || !(typeaheadPopup instanceof HTMLElement)) {
						return;
					}

					const targetElement = typeaheadPopup.querySelector('[role="option"][aria-selected="true"]');
					if (!targetElement || !(targetElement instanceof HTMLElement)) {
						return;
					}

					const testId = targetElement.dataset.testid;
					if (testId === SYNCED_BLOCK_BUTTON_TEST_ID.quickInsertCreate) {
						createSourceQuickInsertMenuExperience.start();
					}
				}
			},
			options: { capture: true },
		});

		return { unbindClickListener, unbindKeydownListener };
	};

	return new SafePlugin({
		key: pluginKey,
		view: (view) => {
			editorViewRef.current = view;

			// Track whether listeners have been bound. When the experiment is
			// ON and the document initially has no synced blocks, we defer
			// binding until `update()` detects that `hasSyncedBlocks` has
			// flipped to `true` (e.g. via paste or collab insert). This avoids
			// the ~2-5 ms TBT cost of capture-phase click/keydown handlers on
			// the ~99.97 % of pages that never use synced blocks (EDITOR-6931).
			let listenersBound = false;
			let unbindClickListener: (() => void) | undefined;
			let unbindKeydownListener: (() => void) | undefined;

			const ensureListenersBound = () => {
				if (listenersBound) {
					return;
				}
				listenersBound = true;
				const unbinders = bindListeners();
				unbindClickListener = unbinders.unbindClickListener;
				unbindKeydownListener = unbinders.unbindKeydownListener;
			};

			if (syncedBlockPluginKey.getState(view.state)?.hasSyncedBlocks || !isPerfExperimentOn) {
				ensureListenersBound();
			}

			return {
				update: (view, prevState) => {
					if (
						!listenersBound &&
						view.state.doc !== prevState.doc &&
						syncedBlockPluginKey.getState(view.state)?.hasSyncedBlocks &&
						isPerfExperimentOn
					) {
						// Bind listeners now that synced blocks are present.
						ensureListenersBound();
					}
				},
				destroy: () => {
					createSourcePrimaryToolbarExperience.abort({ reason: 'editorDestroyed' });
					createSourceBlockMenuExperience.abort({ reason: 'editorDestroyed' });
					createSourceQuickInsertMenuExperience.abort({ reason: 'editorDestroyed' });
					deleteReferenceSyncedBlockExperience.abort({ reason: 'editorDestroyed' });
					deleteSourceSyncedBlockExperience?.abort({ reason: 'editorDestroyed' });
					unsyncReferenceSyncedBlockExperience?.abort({ reason: 'editorDestroyed' });
					unsyncSourceSyncedBlockExperience?.abort({ reason: 'editorDestroyed' });
					syncedLocationsExperience?.abort({ reason: 'editorDestroyed' });
					unbindClickListener?.();
					unbindKeydownListener?.();
				},
			};
		},
	});
};

const isSyncedBlockButtonId = (value: string | undefined): value is SyncedBlockButtonId => {
	return !!value && syncedBlockButtonIds.has(value as SyncedBlockButtonId);
};

type HandleButtonClickProps = {
	button: HTMLButtonElement;
	createSourceBlockMenuExperience: Experience;
	createSourcePrimaryToolbarExperience: Experience;
	createSourceQuickInsertMenuExperience: Experience;
	deleteReferenceSyncedBlockExperience: Experience;
	deleteSourceSyncedBlockExperience?: Experience;
	syncedLocationsExperience?: Experience;
	testId: SyncedBlockButtonId;
	unsyncReferenceSyncedBlockExperience?: Experience;
	unsyncSourceSyncedBlockExperience?: Experience;
};
const handleButtonClick = ({
	testId,
	button,
	createSourcePrimaryToolbarExperience,
	createSourceBlockMenuExperience,
	createSourceQuickInsertMenuExperience,
	deleteReferenceSyncedBlockExperience,
	unsyncReferenceSyncedBlockExperience,
	unsyncSourceSyncedBlockExperience,
	deleteSourceSyncedBlockExperience,
	syncedLocationsExperience,
}: HandleButtonClickProps) => {
	switch (testId) {
		case SYNCED_BLOCK_BUTTON_TEST_ID.primaryToolbarCreate:
			createSourcePrimaryToolbarExperience.start({ forceRestart: true });
			break;
		case SYNCED_BLOCK_BUTTON_TEST_ID.blockMenuCreate:
			createSourceBlockMenuExperience.start({ forceRestart: true });
			break;
		case SYNCED_BLOCK_BUTTON_TEST_ID.quickInsertCreate:
			createSourceQuickInsertMenuExperience.start({ forceRestart: true });
			break;
		case SYNCED_BLOCK_BUTTON_TEST_ID.syncedBlockToolbarReferenceDelete:
			deleteReferenceSyncedBlockExperience.start({ forceRestart: true });
			break;
		case SYNCED_BLOCK_BUTTON_TEST_ID.syncedBlockToolbarReferenceUnsync:
			unsyncReferenceSyncedBlockExperience?.start({ forceRestart: true });
			break;
		case SYNCED_BLOCK_BUTTON_TEST_ID.syncedBlockToolbarSourceUnsync:
			unsyncSourceSyncedBlockExperience?.start({ forceRestart: true });
			break;
		case SYNCED_BLOCK_BUTTON_TEST_ID.syncedBlockToolbarSourceDelete:
			deleteSourceSyncedBlockExperience?.start({ forceRestart: true });
			break;
		case SYNCED_BLOCK_BUTTON_TEST_ID.syncedBlockToolbarSyncedLocationsTrigger:
			// Only track when opening the dropdown
			if (button.getAttribute('aria-pressed') === 'false') {
				syncedLocationsExperience?.start({ forceRestart: true });
			}
			break;
		default: {
			// Exhaustiveness check: if a new SyncedBlockToolbarButtonId is added
			// but not handled above, TypeScript will error here.
			const _exhaustiveCheck: never = testId;
			return _exhaustiveCheck;
		}
	}
};

const isEnterKey = (key: string) => {
	return key === 'Enter';
};

const syncedBlockAddedToDomCheck = (
	refs: {
		containerElement?: HTMLElement;
		popupsMountPoint?: HTMLElement;
		wrapperElement?: HTMLElement;
	},
	editorViewRef?: EditorViewRef,
) =>
	new ExperienceCheckDomMutation({
		onDomMutation: ({ mutations }) => {
			if (mutations.some(isBodiedSyncBlockAddedInMutation)) {
				return { status: 'success' };
			}
			return undefined;
		},
		observeConfig: () => {
			return [
				{
					target: editorViewRef?.current?.dom,
					options: {
						childList: true,
					},
				},
				// When wrapping a node with breakout mark with sync block, breakout dom is reused
				// hence we need to observe subtree to catch sync block mutation
				{
					target: getSelectionAncestorDOM(editorViewRef?.current),
					options: {
						childList: true,
						subtree: true,
					},
				},
			];
		},
	});

const isBodiedSyncBlockAddedInMutation = ({ type, addedNodes }: MutationRecord) => {
	return type === 'childList' && [...addedNodes].some(isBodiedSyncBlockWithinNode);
};

const isBodiedSyncBlockWithinNode = (node?: Node | null) =>
	getNodeQuery('[data-prosemirror-node-name="bodiedSyncBlock"]')(node);

const referenceSyncBlockRemovedFromDomCheck = (
	refs: {
		containerElement?: HTMLElement;
		popupsMountPoint?: HTMLElement;
		wrapperElement?: HTMLElement;
	},
	editorViewRef?: EditorViewRef,
) =>
	new ExperienceCheckDomMutation({
		onDomMutation: ({ mutations }) => {
			if (mutations.some(isSyncBlockRemovedInMutation)) {
				return { status: 'success' };
			}
			return undefined;
		},
		observeConfig: () => {
			return [
				{
					target: editorViewRef?.current?.dom,
					options: {
						childList: true,
					},
				},
						{
							target: getSelectionAncestorDOM(editorViewRef?.current),
							options: {
								childList: true,
								subtree: true,
							},
						},
			];
		},
	});

const isSyncBlockRemovedInMutation = ({ type, removedNodes }: MutationRecord) => {
	return type === 'childList' && [...removedNodes].some(isSyncBlockWithinNode);
};

const isSyncBlockWithinNode = (node?: Node | null) =>
	getNodeQuery('[data-prosemirror-node-name="syncBlock"]')(node);

const syncBlockDeleteConfirmationModalAddedCheck = () =>
	new ExperienceCheckDomMutation({
		onDomMutation: ({ mutations }) => {
			if (mutations.some(isDeleteConfirmationModalAddedInMutation)) {
				return { status: 'success' };
			}
			return undefined;
		},
		observeConfig: () => {
			return {
				target: document.body,
				options: {
					childList: true,
					subtree: true,
				},
			};
		},
	});

const isDeleteConfirmationModalAddedInMutation = ({ type, addedNodes }: MutationRecord) => {
	return type === 'childList' && [...addedNodes].some(isDeleteConfirmationModalWithinNode);
};

const isDeleteConfirmationModalWithinNode = (node?: Node | null) =>
	getNodeQuery('[data-testid="sync-block-delete-confirmation"]')(node);

const syncedLocationsDropdownOpenedCheck = () =>
	new ExperienceCheckDomMutation({
		onDomMutation: ({ mutations }) => {
			if (mutations.some(isSyncedLocationsDropdownErrorInMutation)) {
				return { status: 'failure' };
			}
			if (mutations.some(isSyncedLocationsDropdownAddedInMutation)) {
				return { status: 'success' };
			}
			return undefined;
		},
		observeConfig: () => {
			return {
				target: document.body,
				options: {
					childList: true,
					subtree: true,
				},
			};
		},
	});

const isSyncedLocationsDropdownAddedInMutation = ({ type, addedNodes }: MutationRecord) => {
	return type === 'childList' && [...addedNodes].some(isSyncedLocationsDropdownWithinNode);
};

const isSyncedLocationsDropdownErrorInMutation = ({ type, addedNodes }: MutationRecord) => {
	return type === 'childList' && [...addedNodes].some(isSyncedLocationsDropdownErrorWithinNode);
};

const isSyncedLocationsDropdownWithinNode = (node?: Node | null) => {
	return !!(
		getNodeQuery('[data-testid="synced-locations-dropdown-content"]')(node) ||
		getNodeQuery('[data-testid="synced-locations-dropdown-content-no-results"]')(node)
	);
};

const isSyncedLocationsDropdownErrorWithinNode = (node?: Node | null) => {
	return !!getNodeQuery('[data-testid="synced-locations-dropdown-content-error"]')(node);
};
